import { ReactNode } from 'react';
import type { UploadFile } from 'antd';
import type { FormInstance } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import dayjs from 'dayjs';

/**
 * Props for ExpenseFormFields component
 */
export type ExpenseFormFieldsProps = {
  /** Permissions of user */
  permissions?: {
    isClientAdmin?: boolean;
    isTenantAdmin?: boolean;
  };
  /** User profile information */
  profile: API.ProfileMe;
  /** Expense settings configuration */
  expenseSettings: {
    require_department: boolean;
    require_project: boolean;
    require_xgl: boolean;
    multiple_rows: boolean;
  };
  /** Existing expense record being edited */
  recordToEdit?: API.ExpenseHeaderCollection;
  /** Whether this is the user's own expense */
  isMyExpense: boolean;
  /** Whether using private card */
  isPrivateCard: boolean;
  /** Whether the expense owner can be changed */
  isExpenseOwnerChangeAvailable: boolean;
  /** Handler for owner switch change */
  onOwnerSwitchChange: (checked: boolean) => void;
  /** Handler for private card change */
  onPrivateCardChange: (checked: boolean) => void;
  /** All data and handlers from ExpenseDataProvider */
  setsubmitterInfo: React.Dispatch<
    React.SetStateAction<{
      firstName: string;
      lastName: string;
    }>
  >;
} & ExpenseDataProviderRenderProps;

/**
 * Props for ExpenseDataProvider component
 */
export type ExpenseDataProviderProps = {
  /** Authentication token */
  accessToken: string;
  /** Client ID */
  clientId: string;
  /** User profile information */
  profile: API.ProfileMe;
  /** Expense settings configuration */
  expenseSettings: {
    require_department: boolean;
    require_project: boolean;
    require_xgl: boolean;
    multiple_rows: boolean;
  };
  /** Existing expense record being edited */
  recordToEdit?: API.ExpenseHeaderCollection;
  /** Whether this is the user's own expense */
  isMyExpense: boolean;
  /** Whether using private card */
  isPrivateCard: boolean;
  /** Function to update recordToEdit after expense creation */
  updateRecordToEdit?: (record: API.ExpenseHeaderCollection) => void;
  /** Render prop function that receives all data and handlers */
  children: (props: ExpenseDataProviderRenderProps) => ReactNode;
};

/**
 * Constants for data types available for fetching
 */
export const FETCH_DATA_TYPES = {
  DIMENSIONS: 'dimensions',
  PROJECTS: 'projects',
  COST_TYPES: 'costTypes',
  COMPANY_CARDS: 'companyCards',
  XGL: 'xgl',
} as const;

/**
 * Data types available for fetching - derived from constants
 */
export type FetchDataType = typeof FETCH_DATA_TYPES[keyof typeof FETCH_DATA_TYPES];

/**
 * Generic debounced search handler type for API parameters
 */
export type DebouncedSearchHandler = (params: API.FetchParams) => void;

/**
 * Universal debounced fetch function type
 */
export type DebouncedFetch = (
  dataType: FetchDataType,
  params: API.FetchParams
) => void;

/**
 * Generic data state structure for fetched data with loading and pagination
 * Uses BaseOptionType for stricter typing while remaining compatible with DefaultOptionType
 */
export type DataState = {
  /** Array of data items with label/value structure */
  data: DefaultOptionType[];
  /** Loading state indicator */
  loading: boolean;
  /** Optional pagination information */
  pagination?: API.PaginationResponse;
};

/**
 * Props passed to children render function
 */
export type ExpenseDataProviderRenderProps = {
  // Form management
  form: any;
  submitting: boolean;
  approving: boolean;
  formChanged: boolean;
  headerId: string;
  status: string;
  setFormChanged: React.Dispatch<React.SetStateAction<boolean>>;
  setApproving: React.Dispatch<React.SetStateAction<boolean>>;
  setInitialForm: () => void;
  initializationLoading: boolean;

  // Data and loading states using consolidated DataState pattern
  dimensions: DataState;
  projects: DataState;
  costTypes: DataState;
  companyCards: DataState;
  xgl: DataState;
  users: DataState;

  // Universal search handler
  debouncedFetch: DebouncedFetch;

  // File management
  files: ExpenseFile[];
  setFiles: React.Dispatch<React.SetStateAction<ExpenseFile[]>>;

  // Action handlers
  handleCreateExpense: () => Promise<void>;
  handleUpdateExpense: (submitter?: string) => Promise<void>;
  handleApproveExpense: () => Promise<void>;
};

/**
 * Props for the useExpenseForm custom hook
 * Manages expense form state and validation logic
 */
export type UseExpenseFormProps = {
  /** Client ID for the expense */
  clientId: string;
  /** User profile information */
  profile: API.ProfileMe;
  /** Expense configuration settings */
  expenseSettings: any;
  /** Optional existing expense record for editing */
  recordToEdit?: any;
  /** Whether this is the user's own expense */
  isMyExpense: boolean;
  /** Whether using private card for payment */
  isPrivateCard: boolean;
  /** Function to update recordToEdit after expense creation */
  updateRecordToEdit?: (record: API.ExpenseHeaderCollection) => void;
};

/**
 * Core expense row item data structure representing a single expense entry
 * Contains monetary values split into NOK (Norwegian Kroner) and øre (cents)
 */
export type ExpenseRowItem = {
  /** Øre value (cents) as string */
  ore: string;
  /** NOK value (kroner) as string */
  nok: string;
  /** Expense title/description */
  title: string;
  /** Cost type identifier */
  costType: string;
  /** Date string in ISO format */
  date: string;
  /** Total amount as decimal number */
  amount: number;
  /** Associated expense account ID */
  expense_account_id: string;
  /** Optional unique identifier for the expense row */
  id?: string;
  /** Optional file path for attached receipt/document */
  file_path?: string;
};

/**
 * Expense file relation type - allows relation of files and expense rows
 * The logic behind this type is that when a new row is created, a UUID is assigned to that row.
 * That UUID belongs to the ExpenseFile type as well.
 * A new expense file has a file but no fileList, an expense file from api has a fileList but no file,
 * an update will have both a file and a filelist. Deletion is handled at row removal.
 */
export type ExpenseFile = {
  id: string;
  file?: File | null;
  fileList?: UploadFile[];
};

/**
 * User profile information for display purposes
 */
export type ProfileInfo = {
  /** User's first name */
  firstName: string;
  /** User's last name */
  lastName: string;
};

/**
 * Date range configuration for date pickers
 * Defines the allowable date range for expense entries
 */
export type MaxMinDates = {
  /** Maximum allowable date for expense entries */
  maxDate: dayjs.Dayjs;
  /** Minimum allowable date for expense entries */
  minDate: dayjs.Dayjs;
};

/**
 * Props for the ExpenseEntries component
 * Handles the dynamic table of expense rows with file uploads
 */
export type ExpenseEntriesProps = {
  /** Authentication token for API calls */
  token: string;
  /** Current expense status (draft, submitted, etc.) */
  status: string;
  /** Unique identifier for the expense header */
  headerId: string;
  /** Ant Design form instance for managing form state */
  form: FormInstance<any>;
  /** Array of uploaded files associated with expense rows */
  files: ExpenseFile[];
  /** State setter for managing uploaded files */
  setFiles: React.Dispatch<React.SetStateAction<ExpenseFile[]>>;
  /** State setter to track if form has been modified */
  setFormChanged: React.Dispatch<React.SetStateAction<boolean>>;
  /** Available cost types for selection */
  costTypes: DefaultOptionType[];
  /** Loading state for cost types dropdown */
  costTypesLoading: boolean;
  /** Pagination response for cost types */
  costTypesPagination?: API.PaginationResponse;
  /** Handler for cost type search/filtering */
  onCostTypeSearch: (params: API.FetchParams) => void;
  /** Date range constraints for expense entries */
  maxMinDates: MaxMinDates;
  /** Whether the expense is in approval mode */
  approving: boolean;
  /** Whether to allow multiple expense rows */
  multipleRows: boolean;
};

/**
 * Props for the useExpenseData custom hook
 * Manages fetching of expense-related data and dropdown options
 */
export type UseExpenseDataProps = {
  /** Authentication token for API calls */
  accessToken: string;
  /** Current expense status */
  status: string;
  /** Optional existing expense record for editing */
  recordToEdit?: any;
  /** Client ID for filtering data */
  clientId: string;
};

/**
 * Props for the ExpenseStatus component
 * Displays expense status and submitter information
 */
export type ExpenseStatusProps = {
  /** Current expense status */
  status: string;
  /** Information about who submitted the expense */
  submitterInfo: {
    firstName: string;
    lastName: string;
  };
};

/**
 * Props for the ExpenseActions component
 * Handles save and approve button logic
 */
export type ExpenseActionsProps = {
  /** Current expense status */
  status: string;
  /** Whether form has been modified */
  formChanged: boolean;
  /** Whether expense is currently being submitted */
  submitting: boolean;
  /** Handler for saving expense */
  onSave: () => void;
  /** Handler for approving and submitting expense */
  onApprove: () => void;
};
